/**
 * ParticipationHistoryCard — compact per-study participation summary from
 * crm.study_attempts (Expansion Phase G Profile tab): study title + outcome
 * badge + timing. The live screening workflow stays in the Screening tab;
 * this is the "what studies has this person touched" record view.
 */
import { useCallback, useEffect, useState } from 'react';
import { History } from 'lucide-react';
import api from '../../../services/api';
import { StudyAttempt } from '../../types';
import { attemptStatusMeta, relTime } from '../../lib';
import AttemptDetailModal from '../prescreen/AttemptDetailModal';

interface Props {
  contactId: string;
  reloadToken: number;
}

export default function ParticipationHistoryCard({ contactId, reloadToken }: Props) {
  const [attempts, setAttempts] = useState<StudyAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailAttempt, setDetailAttempt] = useState<{ id: string; title: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/attempts`);
      setAttempts(res.data?.attempts || []);
    } catch {
      /* renders what it has; the next reloadToken retries */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  // ContactDetail is NOT remounted on :id change, so close any open screener
  // when we navigate to a different contact — otherwise the prior contact's
  // prescreen PHI would render under the new contact.
  useEffect(() => { setDetailAttempt(null); }, [contactId]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h3 className="mb-2 inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <History className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        Study participation
      </h3>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : attempts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No study participation yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {attempts.map((a) => {
            const meta = attemptStatusMeta(a.status);
            return (
              <li key={a.id}>
                {/* The whole row opens the recruiter prescreen behind this consideration. */}
                <button
                  type="button"
                  onClick={() => setDetailAttempt({ id: a.id, title: a.study_title || a.study_slug || 'Study' })}
                  className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  title="Open the recruiter prescreen for this study"
                >
                  <div className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {a.study_title || a.study_slug || 'Study'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {a.outcome_at ? `closed ${relTime(a.outcome_at)}` : `started ${relTime(a.started_at)}`}
                    </span>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}>
                    {meta.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {detailAttempt && (
        <AttemptDetailModal
          key={detailAttempt.id}
          attemptId={detailAttempt.id}
          studyTitle={detailAttempt.title}
          onClose={() => setDetailAttempt(null)}
        />
      )}
    </div>
  );
}
